import type { AnchorRect, FrozenPlacement } from './placement';

/**
 * 按钮确认气泡的动效契约（“点哪里就从哪里飞出来、关回哪里去”）。
 *
 * 三条约束决定了这里的实现方式：
 *
 * 1. **动画只改视觉，不改位置**。
 *    最终位置永远由 `computeFrozenPlacement` 决定，动画只碰 `transform` / `opacity`，
 *    一行都不碰 `top` / `left`。因此“动画结束后位置是冻结值”这件事是**结构上成立**的，
 *    不是靠“小心一点”。反过来，一旦用 `top/left` 做动画，就得先测量面板自身，
 *    而“测量 ⇒ 内容变高 ⇒ 位置跳”正是要消灭的那条路。
 *
 * 2. **起点必须是纯几何量**。
 *    起始位置用「锚点矩形左上角」减去「冻结后的面板左上角」，两个量在打开那一刻都已经
 *    是定值，所以起点不需要测量面板尺寸 —— 面板在动画期间长高变宽也不会影响任何计算结果。
 *
 * 3. **时长短**。
 *    140ms：长到眼睛能跟上“从按钮里长出来”的因果，短到关闭动画来不及成为一次误点的背景。
 */

export type OverlayMotion = 'entering' | 'settled' | 'exiting';

/** 进/出各 140ms（需求给的窗口是 120~180ms） */
export const OVERLAY_MOTION_MS = 140;

/**
 * 起始缩放。
 * 只缩到 0.96 而不是 0.5：确认气泡里是要按的按钮，缩太狠会变成“一个玩具从按钮里蹦出来”，
 * 而这是一个即将执行危险动作的界面，动效的分寸必须克制。
 */
export const OVERLAY_FLY_SCALE = 0.96;

export interface FlyOffset {
  readonly x: number;
  readonly y: number;
}

/**
 * 飞入/飞出的平移量：把面板左上角挪到锚点左上角。
 *
 * 居中定位（无锚点）时返回 `null`：那时 `placement.left` 不是像素值（走的是 `left: 50%`），
 * 用它算偏移是错的，此时只保留缩放，不去伪造一个方向。
 */
export function flyOffsetOf(placement: FrozenPlacement, anchor: AnchorRect | null): FlyOffset | null {
  if (anchor === null || placement.centered) return null;
  return { x: Math.round(anchor.left - placement.left), y: Math.round(anchor.top - placement.top) };
}

/**
 * 起始态（也是收尾态）的 transform。
 *
 * 进出用**同一个**目标值，动画才是原路返回：关闭时面板朝锚点收回去，
 * 而不是随便往某个方向淡出 —— “哪里来的哪里去”就是靠这一条成立的。
 */
export function flyTransform(base: string | undefined, offset: FlyOffset | null): string {
  const parts: string[] = [];
  // 居中定位的 `translate(-50%, -50%)` 必须留在最前面：它是定位的一部分，不是动画效果
  if (base !== undefined && base !== '') parts.push(base);
  if (offset !== null) parts.push(`translate(${offset.x}px, ${offset.y}px)`);
  parts.push(`scale(${OVERLAY_FLY_SCALE})`);
  return parts.join(' ');
}

/**
 * 等一帧（并让这一帧真的画出去）之后调用回调。
 *
 * 为什么是**双** rAF：起始态必须先被浏览器排版/绘制一次，`transition` 才有起点；
 * 单次 rAF 在部分浏览器里会被合并进同一帧，结果是动画被整段吞掉 —— 面板直接闪现在终点。
 * 这种 bug 只在真机上看得见（jsdom 不排版），所以宁可多一帧。
 */
export function scheduleAfterPaint(callback: () => void): () => void {
  const raf: ((cb: () => void) => number) | null =
    typeof requestAnimationFrame === 'function' ? (cb) => requestAnimationFrame(cb) : null;

  if (raf === null) {
    // 没有 rAF 的环境（老测试环境、某些 Worker 包装）退化成下一轮宏任务，仍然是“之后”
    const timer = setTimeout(callback, 16);
    return () => clearTimeout(timer);
  }

  let second = 0;
  const first = raf(() => {
    second = raf(callback);
  });
  return () => {
    cancelAnimationFrame(first);
    if (second !== 0) cancelAnimationFrame(second);
  };
}

/**
 * 是否要求减少动态效果。
 *
 * 前庭功能敏感的用户会因为位移/缩放动画眩晕，而这套动效对功能没有任何贡献
 *（位置与顺序信息由 UI 本身表达），所以这一个 `matchMedia` 判断是必须的。
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
